import { Component } from '@theme/component';

/**
 * Mobile drawer navigation for liquid glass header.
 * Collection menus (e.g. Productos) render inline at root; other parents use drill-down.
 *
 * @extends {Component}
 */
class MenuDrawerGlass extends Component {
  /** @type {MutationObserver | undefined} */
  #drawerObserver;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.#showRoot();
    this.#observeDrawerClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawerObserver?.disconnect();
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const submenuTrigger = event.target.closest('[data-menu-drawer-glass-trigger="submenu"]');
    if (submenuTrigger instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#closeLocalization();
      const handle = submenuTrigger.dataset.submenuHandle;
      if (handle) this.#showSubmenu(handle);
      return;
    }

    const backTrigger = event.target.closest('[data-menu-drawer-glass-trigger="back"]');
    if (backTrigger) {
      event.preventDefault();
      this.#closeLocalization();
      this.#showRoot();
    }
  };

  #closeLocalization() {
    const localization = this.closest('.menu-drawer')?.querySelector('menu-drawer-localization-glass-component');
    if (localization && 'hidePanel' in localization && typeof localization.hidePanel === 'function') {
      localization.hidePanel();
    }
  }

  #observeDrawerClose() {
    const details = this.closest('header-drawer')?.querySelector('#Details-menu-drawer-container');
    if (!(details instanceof HTMLDetailsElement)) return;

    this.#drawerObserver = new MutationObserver(() => {
      if (!details.classList.contains('menu-open')) {
        this.#showRoot();
      }
    });

    this.#drawerObserver.observe(details, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  #showRoot() {
    this.querySelectorAll('[data-menu-drawer-glass-view]').forEach((view) => {
      if (!(view instanceof HTMLElement)) return;
      view.hidden = view.dataset.menuDrawerGlassView !== 'root';
    });

    this.querySelectorAll('[data-menu-drawer-glass-trigger="submenu"]').forEach((trigger) => {
      if (trigger instanceof HTMLButtonElement) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    this.#resetNestedMegaMenus();
  }

  /**
   * @param {string} handle
   */
  #showSubmenu(handle) {
    this.querySelectorAll('[data-menu-drawer-glass-view]').forEach((view) => {
      if (!(view instanceof HTMLElement)) return;
      const isRoot = view.dataset.menuDrawerGlassView === 'root';
      const isTarget =
        view.dataset.menuDrawerGlassView === 'submenu' && view.dataset.submenuHandle === handle;
      view.hidden = isRoot || !isTarget;
    });

    const trigger = this.querySelector(
      `[data-menu-drawer-glass-trigger="submenu"][data-submenu-handle="${handle}"]`
    );
    if (trigger instanceof HTMLButtonElement) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    const backButton = this.querySelector(
      `[data-submenu-handle="${handle}"] [data-menu-drawer-glass-trigger="back"]`
    );
    if (backButton instanceof HTMLElement) {
      requestAnimationFrame(() => backButton.focus());
    }
  }

  #resetNestedMegaMenus() {
    this.querySelectorAll('mega-menu-glass').forEach((glass) => {
      if (glass instanceof HTMLElement && 'showCollections' in glass && typeof glass.showCollections === 'function') {
        glass.showCollections();
        return;
      }

      const backButton = glass.querySelector('[data-mega-menu-glass-trigger="back"]');
      if (backButton instanceof HTMLButtonElement) {
        backButton.click();
      }
    });
  }
}

if (!customElements.get('menu-drawer-glass')) {
  customElements.define('menu-drawer-glass', MenuDrawerGlass);
}
